import { useState, useEffect } from "react";
import { FaUsers, FaBookOpen, FaUserFriends, FaCreditCard, FaChartLine, FaMoneyBillWave, FaUserCheck, FaGraduationCap, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

// Import Chart.js and necessary components
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    enrolledUsers: 0,
    pendingUsers: 0
  });

  // Table sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch("https://kaasbackend.vetrinahealthcare.com/lms/reports/students/json/");
        const data = await response.json();
        setUsers(data);
        
        // Calculate statistics
        const totalUsers = data.length;
        const activeUsers = data.filter(user => 
          user.courses_enrolled && !user.courses_enrolled.includes("None")
        ).length;
        const enrolledUsers = data.filter(user => 
          user.courses_enrolled && user.courses_enrolled.length > 0 && !user.courses_enrolled.includes("None")
        ).length;
        const pendingUsers = data.filter(user => 
          user.courses_enrolled && user.courses_enrolled.includes("None")
        ).length;
        
        setStats({
          totalUsers,
          activeUsers,
          enrolledUsers,
          pendingUsers
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Process user data for charts
  const processChartData = () => {
    // Monthly user registration data
    const monthlyRegistrations = {};
    
    users.forEach(user => {
      if (user.join_date) {
        // Extract month from join_date (format: DD-MM-YYYY HH:mm:ss)
        const month = user.join_date.split('-')[1]; // Get month part
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const monthName = monthNames[parseInt(month) - 1] || month;
        
        if (monthlyRegistrations[monthName]) {
          monthlyRegistrations[monthName]++;
        } else {
          monthlyRegistrations[monthName] = 1;
        }
      }
    });
    
    // Course enrollment data
    const courseEnrollments = {};
    
    users.forEach(user => {
      if (user.courses_enrolled && !user.courses_enrolled.includes("None")) {
        user.courses_enrolled.forEach(course => {
          if (courseEnrollments[course]) {
            courseEnrollments[course]++;
          } else {
            courseEnrollments[course] = 1;
          }
        });
      }
    });
    
    // User occupation data
    const occupations = {};
    
    users.forEach(user => {
      const occupation = user.occupation || "Not specified";
      if (occupations[occupation]) {
        occupations[occupation]++;
      } else {
        occupations[occupation] = 1;
      }
    });
    
    return { monthlyRegistrations, courseEnrollments, occupations };
  };

  const { monthlyRegistrations, courseEnrollments, occupations } = processChartData();

  // Chart.js options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  // Line chart data
  const lineChartData = {
    labels: Object.keys(monthlyRegistrations),
    datasets: [
      {
        label: "User Registrations",
        data: Object.values(monthlyRegistrations),
        borderColor: "#7366ff",
        backgroundColor: "rgba(115, 102, 255, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Bar chart data
  const barChartData = {
    labels: Object.keys(courseEnrollments).slice(0, 6), // Top 6 courses
    datasets: [
      {
        label: "Enrollments",
        data: Object.values(courseEnrollments).slice(0, 6),
        backgroundColor: [
          "rgba(115, 102, 255, 0.7)",
          "rgba(255, 99, 132, 0.7)",
          "rgba(54, 162, 235, 0.7)",
          "rgba(75, 192, 192, 0.7)",
          "rgba(153, 102, 255, 0.7)",
          "rgba(255, 159, 64, 0.7)",
        ],
        borderWidth: 1,
      },
    ],
  };

  // Doughnut chart data
  const doughnutChartData = {
    labels: ["Active Users", "Non-Active Users"],
    datasets: [
      {
        data: [stats.activeUsers, stats.pendingUsers],
        backgroundColor: ["#4caf50", "#ff9800"],
        borderWidth: 0,
      },
    ],
  };

  // Radar chart data
  const radarChartData = {
    labels: Object.keys(occupations).slice(0, 6), // Top 6 occupations
    datasets: [
      {
        label: "Users by Occupation",
        data: Object.values(occupations).slice(0, 6),
        backgroundColor: "rgba(115, 102, 255, 0.2)",
        borderColor: "rgba(115, 102, 255, 1)",
        pointBackgroundColor: "rgba(115, 102, 255, 1)",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(115, 102, 255, 1)",
      },
    ],
  };

  // Table sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort data based on configuration
  const sortedData = () => {
    if (!sortConfig.key) return users;
    
    return [...users].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Get current page data
  const currentData = sortedData().slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Render sort icon
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort style={{ marginLeft: '5px', opacity: 0.3 }} />;
    if (sortConfig.direction === 'asc') return <FaSortUp style={{ marginLeft: '5px' }} />;
    return <FaSortDown style={{ marginLeft: '5px' }} />;
  };

  // Sample data for tables
  const recentUsersColumns = [
    { key: "full_name", label: "Name", sortable: true },
    { key: "email", label: "Email", sortable: true },
    { key: "mobile_number", label: "Phone", sortable: true },
    { 
      key: "status", 
      label: "Status", 
      sortable: true,
      render: (row) => {
        const isActive = row.courses_enrolled && !row.courses_enrolled.includes("None");
        const status = isActive ? "Active" : "Pending";
        return (
          <span
            style={{
              padding: "3px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              backgroundColor: isActive ? "#e6f7e6" : "#fff8e6",
              color: isActive ? "#4caf50" : "#ff9800",
            }}
          >
            {status}
          </span>
        );
      },
    },
    { key: "join_date", label: "Join Date", sortable: true },
  ];

  const recentUsersData = currentData.map(user => ({
    ...user,
    status: user.courses_enrolled && !user.courses_enrolled.includes("None") ? "Active" : "Pending"
  }));

  const courseColumns = [
    { key: "name", label: "Course Name", sortable: true },
    { key: "enrollments", label: "Enrollments", sortable: true },
    { key: "completion", label: "Completion Rate", sortable: true },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (row) => (
        <span
          style={{
            padding: "3px 8px",
            borderRadius: "12px",
            fontSize: "12px",
            backgroundColor: row.status === "Active" ? "#e6f7e6" : "#ffe6e6",
            color: row.status === "Active" ? "#4caf50" : "#f44336",
          }}
        >
          {row.status}
        </span>
      ),
    },
  ];

  const courseData = Object.entries(courseEnrollments).map(([name, enrollments]) => ({
    name,
    enrollments,
    completion: Math.floor(Math.random() * 100), // Random completion rate for demo
    status: Math.random() > 0.3 ? "Active" : "Inactive", // Random status for demo
  })).slice(0, 5);

  // Custom Card Component
  const Card = ({ title, children, value, icon, color, trend, style }) => {
    if (value !== undefined) {
      // This is a stat card
      return (
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            ...style,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ color: "#666", fontSize: "14px", marginBottom: "8px" }}>{title}</div>
              <div style={{ fontSize: "28px", fontWeight: "bold", color: color || "#333" }}>{value}</div>
              {trend && (
                <div style={{ fontSize: "12px", color: trend.positive ? "#4caf50" : "#f44336", marginTop: "5px" }}>
                  {trend.positive ? "↑" : "↓"} {trend.value}% from last month
                </div>
              )}
            </div>
            <div
              style={{
                backgroundColor: color ? `${color}20` : "#f5f5f5",
                borderRadius: "10px",
                padding: "10px",
                color: color || "#333",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </div>
          </div>
        </div>
      );
    }

    // This is a regular card
    return (
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "20px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          ...style,
        }}
      >
        {title && <h3 style={{ margin: "0 0 15px 0", color: "#333", fontWeight: "600" }}>{title}</h3>}
        {children}
      </div>
    );
  };

  // Custom Table Component
  const Table = ({ columns, data, pagination = false, itemsPerPage = 5 }) => {
    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #eee" }}>
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    padding: "12px 15px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#666",
                    cursor: column.sortable ? "pointer" : "default",
                    userSelect: "none"
                  }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    {column.label}
                    {column.sortable && renderSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} style={{ borderBottom: "1px solid #eee" }}>
                {columns.map((column) => (
                  <td key={column.key} style={{ padding: "12px 15px" }}>
                    {column.render ? column.render(row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        
        {pagination && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "15px" }}>
            <div style={{ color: "#666", fontSize: "14px" }}>
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, users.length)} of {users.length} entries
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "5px 10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: currentPage === 1 ? "#f5f5f5" : "#fff",
                  color: currentPage === 1 ? "#999" : "#333",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer"
                }}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={currentPage * itemsPerPage >= users.length}
                style={{
                  padding: "5px 10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: currentPage * itemsPerPage >= users.length ? "#f5f5f5" : "#fff",
                  color: currentPage * itemsPerPage >= users.length ? "#999" : "#333",
                  cursor: currentPage * itemsPerPage >= users.length ? "not-allowed" : "pointer"
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "50vh",
        fontSize: "18px",
        color: "#666"
      }}>
        Loading dashboard data...
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <h1 style={{ marginBottom: "20px", color: "#333", fontWeight: "600" }}>Admin Dashboard</h1>

      {/* Stats Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        <Card 
          title="Total Users" 
          value={stats.totalUsers} 
          icon={<FaUsers size={24} />}
          color="#7366ff"
          trend={{ value: 12.5, positive: true }}
        />
        <Card 
          title="Active Users" 
          value={stats.activeUsers} 
          icon={<FaUserCheck size={24} />}
          color="#4caf50"
          trend={{ value: 8.3, positive: true }}
        />
        <Card 
          title="Course Enrollments" 
          value={stats.enrolledUsers} 
          icon={<FaGraduationCap size={24} />}
          color="#ff9800"
          trend={{ value: 5.7, positive: true }}
        />
        <Card 
          title="Non-Active Users" 
          value={stats.pendingUsers} 
          icon={<FaUsers size={24} />}
          color="#f44336"
          trend={{ value: 3.2, positive: false }}
        />
      </div>

      {/* Charts Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        <Card title="User Registration Trend" style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <div style={{ height: "300px" }}>
            <Line data={lineChartData} options={chartOptions} />
          </div>
        </Card>
        <Card title="Course Enrollment Distribution" style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <div style={{ height: "300px" }}>
            <Bar data={barChartData} options={chartOptions} />
          </div>
        </Card>
        <Card title="User Status" style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <div style={{ height: "300px" }}>
            <Doughnut data={doughnutChartData} options={chartOptions} />
          </div>
        </Card>
        <Card title="User Occupations" style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <div style={{ height: "300px" }}>
            <Radar data={radarChartData} options={chartOptions} />
          </div>
        </Card>
      </div>

      {/* Tables Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(500px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        <Card title="Recent Users" style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <Table columns={recentUsersColumns} data={recentUsersData} pagination={true} itemsPerPage={itemsPerPage} />
        </Card>
        <Card title="Popular Courses" style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <Table columns={courseColumns} data={courseData} pagination={true} itemsPerPage={5} />
        </Card>
      </div>

      {/* Performance Metrics */}
      <div style={{ marginBottom: "30px" }}>
        <Card title="Performance Metrics" style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "20px",
            }}
          >
            <div style={{ textAlign: "center", padding: "15px" }}>
              <div
                style={{
                  fontSize: "36px",
                  fontWeight: "bold",
                  color: "#ff5baf",
                  marginBottom: "10px",
                }}
              >
                {stats.enrolledUsers > 0 ? Math.round((stats.activeUsers / stats.enrolledUsers) * 100) : 0}%
              </div>
              <div style={{ color: "#666", fontSize: "14px" }}>Course Completion Rate</div>
            </div>
            <div style={{ textAlign: "center", padding: "15px" }}>
              <div
                style={{
                  fontSize: "36px",
                  fontWeight: "bold",
                  color: "#7366ff",
                  marginBottom: "10px",
                }}
              >
                4.7
              </div>
              <div style={{ color: "#666", fontSize: "14px" }}>Average Course Rating</div>
            </div>
            <div style={{ textAlign: "center", padding: "15px" }}>
              <div
                style={{
                  fontSize: "36px",
                  fontWeight: "bold",
                  color: "#00c292",
                  marginBottom: "10px",
                }}
              >
                {stats.totalUsers > 0 ? Math.round((stats.activeUsers / stats.totalUsers) * 100) : 0}%
              </div>
              <div style={{ color: "#666", fontSize: "14px" }}>User Engagement</div>
            </div>
            <div style={{ textAlign: "center", padding: "15px" }}>
              <div
                style={{
                  fontSize: "36px",
                  fontWeight: "bold",
                  color: "#ff5370",
                  marginBottom: "10px",
                }}
              >
                {stats.totalUsers > 0 ? Math.round((stats.enrolledUsers / stats.totalUsers) * 100) : 0}%
              </div>
              <div style={{ color: "#666", fontSize: "14px" }}>Enrollment Rate</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;